// src/services/api.js

/**
 * =============================================================
 * SONOAI / ECHION HEALTH API CLIENT
 * =============================================================
 */

const BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://echionhealthapp-production.up.railway.app/api';

// =============================================================
// AUTHENTICATION HELPERS
// =============================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const getMultipartAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Accept': '*/*',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

// =============================================================
// ERROR HANDLING
// =============================================================

const friendlyError = (status, path, rawBody) => {
  const body = String(rawBody || '');

  if (
    body.includes('Cannot POST') ||
    body.includes('Cannot PUT') ||
    body.includes('Cannot GET') ||
    body.includes('Cannot DELETE') ||
    body.includes('Cannot PATCH')
  ) {
    return `The endpoint "${path}" is not registered on the backend.`;
  }
  if (status === 400) return body || 'The request was invalid.';
  if (status === 401) return 'Your session has expired. Please log in again.';
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 404) return `Not found: ${path}`;
  if (status === 409) return body || 'The request conflicts with existing data.';
  if (status === 422) return body || 'The supplied data could not be processed.';
  if (status === 429) return 'Too many requests. Please wait and try again.';
  if (status >= 500) return 'The server encountered an error. Please try again later.';

  const stripped = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return stripped.slice(0, 500) || `Request failed with status ${status}`;
};

// =============================================================
// RESPONSE HANDLING
// =============================================================

const handleResponse = async (response) => {
  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const parsed = text ? JSON.parse(text) : null;
      message = parsed?.message || parsed?.error || parsed?.data?.message || text;
    } catch (_) {}
    throw new Error(friendlyError(response.status, response.url, message));
  }

  const contentType = response.headers.get('content-type') || '';

  if (
    contentType.includes('application/pdf') ||
    contentType.includes('application/vnd.openxmlformats') ||
    contentType.includes('application/vnd.ms-word') ||
    contentType.includes('application/octet-stream')
  ) {
    return response.blob();
  }

  const text = await response.text();
  if (!text) return {};
  try { return JSON.parse(text); } catch (_) { return text; }
};

// =============================================================
// BINARY / FILE HELPERS
// =============================================================

const fetchBlob = async (path) => {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(friendlyError(response.status, response.url, text));
  }
  return response.blob();
};

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Fetch any URL as a Blob and return an object URL. Tries the
 * auth-token header first (works for API endpoints), then falls
 * back to a plain fetch (works for public/static URLs).
 */
const resolveImageBlobUrl = async (rawUrl) => {
  if (!rawUrl) throw new Error('No image URL provided.');

  const token = localStorage.getItem('authToken');

  // 1. Try with Authorization header (for backend-protected endpoints).
  try {
    const r = await fetch(rawUrl, {
      method: 'GET',
      headers: {
        Accept: 'image/*, application/octet-stream, */*',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (r.ok) {
      const blob = await r.blob();
      if (blob && blob.size > 0) return URL.createObjectURL(blob);
    }
  } catch (_) {
    // fall through
  }

  // 2. Try without any headers (for public / pre-signed URLs).
  const r2 = await fetch(rawUrl, { method: 'GET' });
  if (!r2.ok) {
    const text = await r2.text().catch(() => '');
    throw new Error(friendlyError(r2.status, r2.url, text));
  }
  const blob2 = await r2.blob();
  if (!blob2 || blob2.size === 0) {
    throw new Error('Received empty image blob.');
  }
  return URL.createObjectURL(blob2);
};

// =============================================================
// API
// =============================================================

export const api = {

  // ── AUTH ──
  login: async (data) => {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  refresh: async (refreshToken) => {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    return handleResponse(response);
  },

  logout: async (refreshToken) => {
    const response = await fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ refreshToken })
    });
    return handleResponse(response);
  },

  register: async (data) => {
    const response = await fetch(`${BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  verifyEmail: async (token) => {
    const response = await fetch(
      `${BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  resendVerification: async (email) => {
    const response = await fetch(
      `${BASE_URL}/auth/resend-verification?email=${encodeURIComponent(email)}`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getMe: async () => {
    const response = await fetch(`${BASE_URL}/auth/profile`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateUserProfile: async (_userId, data) => {
    const response = await fetch(`${BASE_URL}/auth/profile`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  completeProfile: async (data) => {
    const response = await fetch(`${BASE_URL}/auth/complete-profile`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getPermissions: async () => {
    const response = await fetch(`${BASE_URL}/auth/permissions`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ── MFA ──
  getMfaStatus: async () => {
    const response = await fetch(`${BASE_URL}/auth/mfa`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  setupMfa: async () => {
    const response = await fetch(`${BASE_URL}/auth/mfa/setup`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  enableMfa: async (code) => {
    const response = await fetch(`${BASE_URL}/auth/mfa/enable`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code })
    });
    return handleResponse(response);
  },

  disableMfa: async (code) => {
    const response = await fetch(`${BASE_URL}/auth/mfa/disable`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code })
    });
    return handleResponse(response);
  },

  // ── PASSWORD ──
  changePassword: async (userId, oldPassword, newPassword) => {
    const response = await fetch(`${BASE_URL}/users/${userId}/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ oldPassword, newPassword })
    });
    return handleResponse(response);
  },

  // ── ADMIN ──
  getDashboardStats: async () => {
    const response = await fetch(`${BASE_URL}/admin/dashboard`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAllUsers: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.role) query.append('role', params.role);
    if (params.locked !== undefined) query.append('locked', params.locked);
    if (params.verified !== undefined) query.append('verified', params.verified);
    query.append('page', params.page !== undefined ? params.page : 0);
    query.append('size', params.size !== undefined ? params.size : 50);

    const response = await fetch(`${BASE_URL}/admin/users?${query.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getUserDetails: async (userId) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createUser: async (userData) => {
    const response = await fetch(`${BASE_URL}/admin/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },

  deleteUser: async (userId) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  lockUser: async (userId) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}/lock`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  unlockUser: async (userId) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}/unlock`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateUserRole: async (userId, role) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role })
    });
    return handleResponse(response);
  },

  updateSignaturePermission: async (userId, canUploadSignature) => {
    const response = await fetch(
      `${BASE_URL}/admin/users/${userId}/signature-permission`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ canUploadSignature })
      }
    );
    return handleResponse(response);
  },

  deactivateUser: async (userId) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}/deactivate`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  reactivateUser: async (userId) => {
    const response = await fetch(`${BASE_URL}/admin/users/${userId}/reactivate`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAuditLogs: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.action) query.append('action', params.action);
    if (params.userEmail) query.append('userEmail', params.userEmail);
    if (params.success !== undefined) query.append('success', params.success);
    if (params.startDate) query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    query.append('page', params.page !== undefined ? params.page : 0);
    query.append('size', params.size !== undefined ? params.size : 50);

    const response = await fetch(`${BASE_URL}/admin/audit-logs?${query.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getUserAuditLogs: async (userId, params = {}) => {
    const query = new URLSearchParams();
    query.append('page', params.page !== undefined ? params.page : 0);
    query.append('size', params.size !== undefined ? params.size : 50);

    const response = await fetch(
      `${BASE_URL}/admin/audit-logs/user/${userId}?${query.toString()}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getAuditActions: async () => {
    const response = await fetch(`${BASE_URL}/admin/audit-logs/actions`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  rebuildSearchVectors: async () => {
    const response = await fetch(`${BASE_URL}/admin/rebuild-search-vectors`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getColleagues: async (search = '', page = 0, size = 200) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(size)
    });
    if (search) query.append('search', search);

    const response = await fetch(
      `${BASE_URL}/collaboration/colleagues?${query.toString()}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    const json = await handleResponse(response);
    return json?.data?.content || json?.data || [];
  },

  verifyAiProviders: async () => {
    const response = await fetch(`${BASE_URL}/admin/ai/verify`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getAiUsageReport: async (windowDays = 30) => {
    const response = await fetch(
      `${BASE_URL}/admin/ai/usage?windowDays=${encodeURIComponent(windowDays)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // ── PLATFORM ──
  listOrganizations: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.tier) query.append('tier', params.tier);
    if (params.status) query.append('status', params.status);
    query.append('page', params.page !== undefined ? params.page : 0);
    query.append('size', params.size !== undefined ? params.size : 50);

    const response = await fetch(
      `${BASE_URL}/admin/platform/organizations?${query.toString()}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  createOrganization: async (data) => {
    const response = await fetch(`${BASE_URL}/admin/platform/organizations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getOrganizationDetail: async (orgId) => {
    const response = await fetch(
      `${BASE_URL}/admin/platform/organizations/${orgId}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  updateOrganizationStatus: async (orgId, status, reason) => {
    const response = await fetch(
      `${BASE_URL}/admin/platform/organizations/${orgId}/status`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status, reason })
      }
    );
    return handleResponse(response);
  },

  impersonateUser: async (userId, reason) => {
    const response = await fetch(
      `${BASE_URL}/admin/platform/impersonate/${userId}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason })
      }
    );
    return handleResponse(response);
  },

  stopImpersonating: async (impersonationId) => {
    const qs = impersonationId
      ? `?impersonationId=${encodeURIComponent(impersonationId)}`
      : '';
    const response = await fetch(
      `${BASE_URL}/admin/platform/impersonate/stop${qs}`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  testEmailDelivery: async (recipient) => {
    const response = await fetch(`${BASE_URL}/admin/platform/email/test`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recipient })
    });
    return handleResponse(response);
  },

  getPlatformOverview: async () => {
    const response = await fetch(`${BASE_URL}/admin/platform/overview`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPlatformHealth: async () => {
    const response = await fetch(`${BASE_URL}/admin/platform/health`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getPlatformAudit: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.action) query.append('action', params.action);
    if (params.userEmail) query.append('userEmail', params.userEmail);
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.success !== undefined) query.append('success', params.success);
    if (params.impersonatedOnly !== undefined) query.append('impersonatedOnly', params.impersonatedOnly);
    if (params.startDate) query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    query.append('page', params.page !== undefined ? params.page : 0);
    query.append('size', params.size !== undefined ? params.size : 50);

    const response = await fetch(
      `${BASE_URL}/admin/platform/audit?${query.toString()}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // ── AI REPORT ──
  generateReport: async (data) => {
    const response = await fetch(`${BASE_URL}/generate-report`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  suggestImpression: async (data) => {
    const response = await fetch(`${BASE_URL}/generate-report/suggest-impression`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  improveWording: async (data) => {
    const response = await fetch(`${BASE_URL}/generate-report/grammar-check`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  grammarCheck: async (data) => api.improveWording(data),

  // ── AI SEARCH ──
  searchVaultSemantic: async (query, { scanType, page = 0, size = 20 } = {}) => {
    if (query === undefined || query === null || !String(query).trim()) {
      throw new Error('Please enter a search query.');
    }
    const params = new URLSearchParams();
    params.append('query', String(query).trim());
    params.append('page', String(page));
    params.append('size', String(size));
    if (scanType) params.append('scanType', scanType);

    const response = await fetch(
      `${BASE_URL}/ai/search/vault?${params.toString()}`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  searchTerminology: async ({ query, scanType, limit = 1 } = {}) => {
    if (query === undefined || query === null || !String(query).trim()) {
      throw new Error('Please enter a terminology search query.');
    }
    const body = {
      query: String(query).trim(),
      ...(scanType ? { scanType } : {}),
      ...(limit !== undefined && limit !== null ? { limit: Number(limit) } : {})
    };
    const response = await fetch(`${BASE_URL}/ai/search/terminology`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  },

  // ── REPORTS ──
  saveReport: async (reportData) => {
    const response = await fetch(`${BASE_URL}/vault/reports`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(reportData)
    });
    return handleResponse(response);
  },

  getReportContent: async (reportId) => {
    const response = await fetch(`${BASE_URL}/vault/reports/${reportId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const json = await handleResponse(response);
    return json?.data || json;
  },

  updateReport: async (reportId, reportData) => {
    const response = await fetch(`${BASE_URL}/vault/reports/${reportId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(reportData)
    });
    return handleResponse(response);
  },

  deleteReport: async (reportId) => {
    const response = await fetch(`${BASE_URL}/vault/reports/${reportId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  searchReports: async (keywordOrFilters) => {
    const body =
      typeof keywordOrFilters === 'string'
        ? { query: keywordOrFilters, page: 0, size: 50 }
        : { ...keywordOrFilters };

    const response = await fetch(`${BASE_URL}/vault/reports/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    const json = await handleResponse(response);
    return json?.data?.content || [];
  },

  searchReportsPage: async (filters = {}) => {
    const response = await fetch(`${BASE_URL}/vault/reports/search`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(filters)
    });
    return handleResponse(response);
  },

  getRecentReports: async () => {
    const response = await fetch(`${BASE_URL}/vault/reports/recent`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getReportHelpers: async (data) => {
    const response = await fetch(`${BASE_URL}/vault/reports/helpers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getDifferentials: async (data) => {
    return api.getReportHelpers({
      scanType: data?.scanType || 'GENERAL',
      findings: data?.findings || data?.finding || ''
    });
  },

  askAiChat: async (data) => {
    return api.getReportHelpers({
      scanType: data?.scanType || 'GENERAL',
      findings: data?.findings || data?.prompt || ''
    });
  },

  autosaveReport: async (reportId, data) => {
    const response = await fetch(
      `${BASE_URL}/vault/reports/${reportId}/autosave`,
      { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }
    );
    return handleResponse(response);
  },

  previewReport: async (reportId, data) => {
    const response = await fetch(
      `${BASE_URL}/vault/reports/${reportId}/preview`,
      { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }
    );
    return handleResponse(response);
  },

  finalizeReport: async (reportId, data) => {
    const response = await fetch(
      `${BASE_URL}/vault/reports/${reportId}/finalize`,
      { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }
    );
    return handleResponse(response);
  },

  restoreReportVersion: async (reportId, versionNumber) => {
    const response = await fetch(
      `${BASE_URL}/vault/reports/${reportId}/versions/${versionNumber}/restore`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getReportVersions: async (reportId, page = 0, size = 20) => {
    const response = await fetch(
      `${BASE_URL}/vault/reports/${reportId}/versions?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getReportVersion: async (reportId, versionNumber) => {
    const response = await fetch(
      `${BASE_URL}/vault/reports/${reportId}/versions/${versionNumber}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  printReportPdf: async (reportId) =>
    fetchBlob(`/vault/reports/${reportId}/print`),

  downloadReportFile: async (reportId, filename = 'report-file') => {
    const blob = await fetchBlob(`/vault/reports/${reportId}/download`);
    triggerBlobDownload(blob, filename);
    return blob;
  },

  downloadReportPdf: async (reportId, filename = 'report.pdf') => {
    const blob = await fetchBlob(`/vault/reports/${reportId}/download-pdf`);
    triggerBlobDownload(blob, filename);
    return blob;
  },

  downloadReportHl7: async (reportId, filename = 'report.hl7') => {
    const blob = await fetchBlob(`/vault/reports/${reportId}/download-hl7`);
    triggerBlobDownload(blob, filename);
    return blob;
  },

  downloadReportDocx: async (reportId, filename = 'report.docx') => {
    const blob = await fetchBlob(`/vault/reports/${reportId}/download-docx`);
    triggerBlobDownload(blob, filename);
    return blob;
  },

  // ── VAULT UPLOADS ──
  uploadVaultDocument: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${BASE_URL}/vault/upload`, {
      method: 'POST',
      headers: getMultipartAuthHeaders(),
      body: formData
    });
    return handleResponse(response);
  },

  uploadVaultDocumentsBatch: async (files, { folderId, scanType } = {}) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('files', file));
    if (folderId !== null && folderId !== undefined) {
      formData.append('folderId', String(folderId));
    }
    if (scanType) formData.append('scanType', scanType);
    const response = await fetch(`${BASE_URL}/vault/upload-batch`, {
      method: 'POST',
      headers: getMultipartAuthHeaders(),
      body: formData
    });
    return handleResponse(response);
  },

  // ── TEMPLATES ──
  getTemplates: async () => {
    const response = await fetch(`${BASE_URL}/vault/templates`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  saveTemplate: async (data) => {
    const response = await fetch(`${BASE_URL}/vault/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  updateTemplate: async (templateId, data) => {
    const response = await fetch(`${BASE_URL}/vault/templates/${templateId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteTemplate: async (templateId, cascadeSharedAccess = false) => {
    const params = new URLSearchParams();
    params.append('cascadeSharedAccess', String(cascadeSharedAccess));
    const response = await fetch(
      `${BASE_URL}/vault/templates/${templateId}?${params.toString()}`,
      { method: 'DELETE', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  setTemplateFavorite: async (templateId, value) => {
    const params = new URLSearchParams();
    params.append('value', String(value));
    const response = await fetch(
      `${BASE_URL}/vault/templates/${templateId}/favorite?${params.toString()}`,
      { method: 'PUT', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  shareTemplate: async (templateId, recipientUserIds) => {
    const response = await fetch(
      `${BASE_URL}/vault/templates/${templateId}/share`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ recipientUserIds })
      }
    );
    return handleResponse(response);
  },

  duplicateTemplate: async (templateId, newName) => {
    const params = new URLSearchParams();
    if (newName) params.append('newName', newName);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(
      `${BASE_URL}/vault/templates/${templateId}/duplicate${query}`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  restoreTemplateVersion: async (templateId, versionNumber) => {
    const response = await fetch(
      `${BASE_URL}/vault/templates/${templateId}/versions/${versionNumber}/restore`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getTemplateVersions: async (templateId, page = 0, size = 20) => {
    const response = await fetch(
      `${BASE_URL}/vault/templates/${templateId}/versions?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  uploadTemplateFile: async (file, name, scanType) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    if (scanType) params.append('scanType', scanType);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(
      `${BASE_URL}/vault/templates/upload${query}`,
      { method: 'POST', headers: getMultipartAuthHeaders(), body: formData }
    );
    return handleResponse(response);
  },

  importTemplatesJson: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${BASE_URL}/vault/templates/import/json`, {
      method: 'POST',
      headers: getMultipartAuthHeaders(),
      body: formData
    });
    return handleResponse(response);
  },

  importTemplatesCsv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${BASE_URL}/vault/templates/import/csv`, {
      method: 'POST',
      headers: getMultipartAuthHeaders(),
      body: formData
    });
    return handleResponse(response);
  },

  bulkUploadTemplates: async (files) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('files', file));
    const response = await fetch(`${BASE_URL}/vault/templates/bulk-upload`, {
      method: 'POST',
      headers: getMultipartAuthHeaders(),
      body: formData
    });
    return handleResponse(response);
  },

  getRecentTemplates: async () => {
    const response = await fetch(`${BASE_URL}/vault/templates/recent`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getFavoriteTemplates: async () => {
    const response = await fetch(`${BASE_URL}/vault/templates/favorites`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getTemplateAnalytics: async () => {
    const response = await fetch(`${BASE_URL}/vault/templates/analytics`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  searchTemplates: async (filters = {}) => {
    const params = new URLSearchParams({ page: '0', size: '50' });
    const response = await fetch(
      `${BASE_URL}/vault/templates/search?${params.toString()}`,
      { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(filters) }
    );
    return handleResponse(response);
  },

  // ── FOLDERS ──
  getRootFolders: async (scanType) => {
    const qs = scanType ? `?scanType=${encodeURIComponent(scanType)}` : '';
    const response = await fetch(`${BASE_URL}/vault/folders${qs}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  createFolder: async (data) => {
    const response = await fetch(`${BASE_URL}/vault/folders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getSubfolders: async (folderId, scanType) => {
    const qs = scanType ? `?scanType=${encodeURIComponent(scanType)}` : '';
    const response = await fetch(
      `${BASE_URL}/vault/folders/${folderId}/subfolders${qs}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // ── SCAN TYPES ──
  getScanTypes: async () => {
    const response = await fetch(`${BASE_URL}/vault/scan-types`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getScanTypeDefinition: async (scanType) => {
    const response = await fetch(`${BASE_URL}/vault/scan-types/${scanType}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ── SIGNATURES ──
  getSignatures: async () => {
    const response = await fetch(`${BASE_URL}/signatures`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  uploadSignature: async (file, label = 'Default Signature', isDefault = true) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({
      label,
      isDefault: String(isDefault)
    });
    const response = await fetch(
      `${BASE_URL}/signatures?${params.toString()}`,
      { method: 'POST', headers: getMultipartAuthHeaders(), body: formData }
    );
    return handleResponse(response);
  },

  updateSignature: async (id, data) => {
    const response = await fetch(`${BASE_URL}/signatures/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteSignature: async (id) => {
    const response = await fetch(`${BASE_URL}/signatures/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ── ORG BRANDING ──
  getOrganizationBranding: async () => {
    const response = await fetch(`${BASE_URL}/org/branding`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  updateOrganizationBranding: async (data) => {
    const response = await fetch(`${BASE_URL}/org/branding`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  uploadLetterhead: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${BASE_URL}/org/letterhead`, {
      method: 'POST',
      headers: getMultipartAuthHeaders(),
      body: formData
    });
    return handleResponse(response);
  },

  getLetterheadPreview: async () => {
    const response = await fetch(`${BASE_URL}/org/letterhead/preview`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  // ── RESOLVE LETTERHEAD / SIGNATURE INTO BLOB: URLs ──
  // These make the images actually renderable, because they attach
  // the auth header (which <img src> cannot do) and turn the response
  // into a same-origin blob URL that html2canvas can also render.
  fetchLetterheadBlobUrl: async () => {
    // 1. Get the branding record (JSON) to know where the letterhead lives.
    const meta = await api.getOrganizationBranding();
    const data = meta?.data || meta;
    const rawUrl = data?.letterheadUrl || data?.url;

    if (!rawUrl) {
      throw new Error('No letterhead URL available.');
    }

    // 2. Fetch the bytes with auth and return a blob URL.
    return resolveImageBlobUrl(rawUrl);
  },

  fetchSignatureBlobUrl: async (preferredImageUrl) => {
    // 1. If no URL given, fetch the user's signatures list to find one.
    let rawUrl = preferredImageUrl;

    if (!rawUrl) {
      const list = await api.getSignatures();
      const signatures = list?.data || [];
      if (Array.isArray(signatures) && signatures.length > 0) {
        const pick =
          signatures.find((s) => s.isDefault) || signatures[0];
        rawUrl = pick?.imageUrl || '';
      }
    }

    if (!rawUrl) {
      throw new Error('No signature image available.');
    }

    // 2. Fetch the bytes with auth and return a blob URL.
    return resolveImageBlobUrl(rawUrl);
  },

  // ── COLLABORATION ──
  getSharedWithMe: async (page = 0, size = 50) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/shared-with-me?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    const json = await handleResponse(response);
    return json?.data?.content || json?.data || [];
  },

  getMyShares: async (page = 0, size = 50) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/my-shares?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    const json = await handleResponse(response);
    return json?.data?.content || json?.data || [];
  },

  getSharedScan: async (id) => {
    const response = await fetch(`${BASE_URL}/collaboration/${id}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getComments: async (scanId, page = 0, size = 50) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/${scanId}/comments?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    const json = await handleResponse(response);
    return json?.data?.content || json?.data || [];
  },

  addComment: async (scanId, data) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/${scanId}/comments`,
      { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }
    );
    return handleResponse(response);
  },

  resolveCase: async (scanId, resolutionNotes) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/${scanId}/resolve`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ resolutionNotes })
      }
    );
    return handleResponse(response);
  },

  shareScan: async (data) => {
    const response = await fetch(`${BASE_URL}/collaboration/share`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  shareCase: async (formData) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/share-with-image`,
      { method: 'POST', headers: getMultipartAuthHeaders(), body: formData }
    );
    return handleResponse(response);
  },

  getSharedScanAuditTrail: async (id, page = 0, size = 50) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/${id}/audit-trail?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  // ── NOTIFICATIONS ──
  fetchNotifications: async () => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications/unread`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getUnreadNotifications: async () => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications/unread`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getUnreadCount: async () => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications/unread-count`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getNotificationsPage: async (page = 0, size = 50) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`,
      { method: 'GET', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  markAsRead: async (notificationId) => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications/${notificationId}/read`,
      { method: 'PUT', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  markAllAsRead: async () => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications/read-all`,
      { method: 'PUT', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getNotificationStreamToken: async () => {
    const response = await fetch(
      `${BASE_URL}/collaboration/notifications/stream-token`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  subscribeNotifications: async () => {
    let token = null;
    try {
      const tokenRes = await api.getNotificationStreamToken();
      token = tokenRes?.data?.token || null;
    } catch (e) {
      token = localStorage.getItem('authToken');
    }
    const url =
      `${BASE_URL}/collaboration/notifications/stream` +
      (token ? `?token=${encodeURIComponent(token)}` : '');
    return new EventSource(url);
  },

  // ── BILLING ──
  getBillingPlan: async () => {
    const response = await fetch(`${BASE_URL}/billing/plan`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  getBillingUsage: async () => {
    const response = await fetch(`${BASE_URL}/billing/usage`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  requestUpgrade: async (requestedTier, reason) => {
    const response = await fetch(`${BASE_URL}/billing/upgrade-request`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ requestedTier, reason })
    });
    return handleResponse(response);
  },

  upgradeSubscription: async (organizationId, subscriptionTier) => {
    const qs = organizationId
      ? `?organizationId=${encodeURIComponent(organizationId)}`
      : '';
    const response = await fetch(`${BASE_URL}/billing/upgrade${qs}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ subscriptionTier })
    });
    return handleResponse(response);
  },

  applyBillingAddons: async (organizationId, data) => {
    const qs = organizationId
      ? `?organizationId=${encodeURIComponent(organizationId)}`
      : '';
    const response = await fetch(`${BASE_URL}/billing/addons${qs}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  // ── ANALYTICS ──
  getAnalyticsDashboard: async () => {
    const response = await fetch(`${BASE_URL}/analytics/dashboard`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  }
};

export default api;